import React from 'react';

import Popup from '../../../common/Popup/Popup.module';
import type { RdvItem } from '../types';

interface ScheduledAppointmentsProps {
  items: RdvItem[];
  loading?: boolean;
  onUpdate: (id: string, patch: Partial<RdvItem>) => void;
  onDelete: (id: string) => void;
  onEdit?: (rdv: RdvItem) => void;
  bannerMessage?: string | null;
  onDismissBanner?: () => void;
  highlightId?: string;
}

const formatDateFR = (dateStr: string) => {
  // Parse YYYY-MM-DD without timezone shift
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateStr; }
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(year, month - 1, day, 12, 0, 0); // noon to avoid DST edge issues
  return d.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

function ScheduledAppointments({ items, loading, onUpdate, onDelete, onEdit, bannerMessage, onDismissBanner, highlightId }: ScheduledAppointmentsProps) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [targetId, setTargetId] = React.useState<string | null>(null);
  const target = React.useMemo(() => items.find(i => i.id === targetId) || null, [items, targetId]);

  const SkeletonRow = () => (
    <div className="grid animate-pulse items-center gap-4 p-5 md:grid-cols-6">
      <div>
        <div className="mb-2 h-4 w-40 rounded bg-slate-200" />
        <div className="h-3 w-20 rounded bg-slate-200" />
      </div>
      <div>
        <div className="mb-1 h-4 w-40 rounded bg-slate-200" />
        <div className="mb-1 h-3 w-24 rounded bg-slate-200" />
        <div className="h-3 w-32 rounded bg-slate-200" />
      </div>
      <div>
        <div className="mb-1 h-4 w-24 rounded bg-slate-200" />
        <div className="h-3 w-24 rounded bg-slate-200" />
      </div>
      <div>
        <div className="mb-1 h-4 w-32 rounded bg-slate-200" />
        <div className="h-3 w-28 rounded bg-slate-200" />
      </div>
      <div>
        <div className="mb-2 h-5 w-24 rounded-full bg-slate-200" />
        <div className="h-4 w-20 rounded bg-slate-200" />
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="h-7 w-24 rounded-lg bg-slate-200" />
        <div className="h-7 w-24 rounded-lg bg-slate-200" />
        <div className="h-7 w-24 rounded-lg bg-slate-200" />
      </div>
    </div>
  );

  return (
    <section className="rounded-xl bg-white shadow-md">
      <div className="border-b px-5 py-4">
        <h2 className="text-lg font-bold text-rgpd-blue"><span role="img" aria-label="calendar">📅</span> Rendez-vous Programmés</h2>
      </div>
      {bannerMessage && (
        <div className="mb-4 px-5 pt-4">
          <div className="flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-sm text-emerald-900">
              <span className="font-semibold"><span role="img" aria-label="check">✅</span> RDV créé :</span> {bannerMessage}
            </div>
            {onDismissBanner && (
              <button type="button" onClick={onDismissBanner} className="rounded-md border border-emerald-300 bg-white/60 px-2 py-1 text-xs text-emerald-800">Fermer</button>
            )}
          </div>
        </div>
      )}
      <div className="divide-y">
        {Boolean(loading) && (
          <>
            {Array.from({ length: 5 }).map((_, i) => (<SkeletonRow key={`rdv-skel-${i}`} />))}
          </>
        )}
        {!loading && items.length === 0 && (
          <div className="p-5 text-slate-600">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="font-semibold text-blue-900"><span role="img" aria-label="info">ℹ️</span> Aucun rendez-vous programmé</div>
              <p className="text-sm text-blue-900/80">Les rendez-vous que vous programmerez apparaîtront ici.</p>
            </div>
          </div>
        )}
        {!loading && items.map((rdv) => (
          <div key={rdv.id} className={['p-5 grid md:grid-cols-6 gap-4 items-center', highlightId === rdv.id ? 'bg-emerald-50/60' : ''].join(' ')}>
            <div>
              <div className="font-bold text-rgpd-blue">{formatDateFR(rdv.date)}</div>
              <div className="font-semibold text-emerald-700">{rdv.heure}</div>
            </div>
            <div>
              <div className="font-bold">{rdv.contact.civilite} {rdv.contact.nom}</div>
              <div className="text-xs text-slate-600">{rdv.contact.tel}</div>
              <div className="text-xs text-slate-500">{rdv.contact.email}</div>
            </div>
            <div>
              <div className="font-bold">{rdv.demande === 'vente' ? (<span><span role="img" aria-label="house">🏠</span> Vente</span>) : (<span><span role="img" aria-label="key">🔑</span> Location</span>)}</div>
              <div className="text-xs text-slate-600">{rdv.bien.type}</div>
            </div>
            <div>
              <div className="font-bold">{rdv.assigneA.nom}</div>
              <div className="text-xs text-slate-600">{rdv.assigneA.role}</div>
            </div>
            <div>
              <div className={[
                'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold',
                rdv.statut === 'nouveau' ? 'bg-amber-100 text-amber-800' : rdv.statut === 'programme' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800',
              ].join(' ')}>
                {rdv.statut === 'nouveau' ? 'Nouveau' : rdv.statut === 'programme' ? 'Programmé' : 'Effectué'}
              </div>
              <div className="mt-1 text-[11px]">
                {rdv.emailSent ? (
                  <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800"><span role="img" aria-label="check">✅</span> Email envoyé</span>
                ) : rdv.emailPending ? (
                  <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-amber-800"><span role="img" aria-label="hourglass">⏳</span> En cours</span>
                ) : (
                  <span className="inline-block rounded-full bg-rose-100 px-2 py-0.5 text-rose-800"><span role="img" aria-label="cross">❌</span> Non envoyé</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs text-white disabled:opacity-60"
                disabled={rdv.emailSent || rdv.emailPending}
                onClick={() => onUpdate(rdv.id, { emailPending: true })}
              ><span role="img" aria-label="email">📧</span> Confirmer</button>
              <button
                className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs text-white"
                onClick={() => onEdit?.(rdv)}
              ><span role="img" aria-label="pencil">✏️</span> Modifier</button>
              <button
                className="rounded-lg !bg-rose-600 px-3 py-1.5 text-xs text-white"
                onClick={() => { setTargetId(rdv.id); setConfirmOpen(true); }}
              ><span role="img" aria-label="cross">❌</span> Annuler</button>
            </div>
          </div>
        ))}
      </div>
      <Popup
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setTargetId(null); }}
        title={<div className="flex items-center gap-2"><span role="img" aria-label="warning">⚠️</span> Confirmation d'annulation</div>}
        additionalClass="w-full max-w-md"
      >
        {target && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="mb-1 font-semibold text-amber-900">Voulez-vous vraiment annuler ce rendez-vous ?</div>
              <ul className="list-disc pl-5 text-sm text-amber-900/90">
                <li><strong>Date :</strong> {formatDateFR(target.date)} à {target.heure}</li>
                <li><strong>Contact :</strong> {target.contact.civilite} {target.contact.nom}</li>
                <li><strong>Adresse :</strong> {target.bien.adresse}</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-slate-700"
                onClick={() => { setConfirmOpen(false); setTargetId(null); }}
              >Annuler</button>
              <button
                type="button"
                className="rounded-md bg-rose-600 px-4 py-2 text-white hover:bg-rose-700"
                onClick={() => { if (targetId) { onDelete(targetId); } setConfirmOpen(false); setTargetId(null); }}
              >Confirmer l'annulation</button>
            </div>
          </div>
        )}
      </Popup>
    </section>
  );
}

export default ScheduledAppointments;


